/**
 * SchadenverlaufSection — the Police-Detail «Schadenverlauf» panel (P2 Placement Slice 6, Ch 7.2).
 *
 * Replaces the Slice-2 SchadenverlaufPlaceholder behind the SAME `policen.risiko_deckung.schadenverlauf`
 * Kachel key + the same `pr-schadenverlauf` test id. Shows the newest entry (SchadenverlaufCard), a
 * «Verlauf anzeigen» toggle for the older ones, the manual «Schadenverlauf neu einholen» button
 * (shown for a writer, disabled with a hint when no provider is configured), and a Rohdaten dialog.
 * The server owns every decision;
 * the UI only triggers + renders and maps the typed 503/502/409 errors to German inline messages.
 */

import { useState } from 'react';
import { SCHADENVERLAUF_NICHT_EINGEHOLT_HINWEIS, SCHADENVERLAUF_STATUS_LABELS } from '@brokly/shared';
import { chf, pct, dateShortDe } from '../../../../lib/format';
import { ApiError } from '../../../../lib/api-client';
import { Button } from '../../../../components/ui/Button';
import { Dialog } from '../../../administration/components';
import {
  usePoliceClaimsHistory,
  useRetrieveClaimsHistory,
  usePoliceClaimsRohdaten,
} from '../../hooks/usePoliceRisiko';
import { SchadenverlaufCard } from './SchadenverlaufCard';

function retrieveErrorMessage(err: unknown): string {
  const code = err instanceof ApiError ? err.code : undefined;
  if (code === 'provider_not_configured') return 'Kein Schadenauskunftsdienst konfiguriert.';
  if (code === 'provider_unavailable') return 'Schadenauskunftsdienst nicht erreichbar.';
  if (code === 'retrieval_conflict') return 'Gleichzeitige Abfrage - bitte neu laden.';
  return 'Schadenverlauf konnte nicht eingeholt werden.';
}

export function SchadenverlaufSection({ policyId, canWrite }: { policyId: string; canWrite: boolean }) {
  const query = usePoliceClaimsHistory(policyId);
  const retrieve = useRetrieveClaimsHistory(policyId);
  const [showVerlauf, setShowVerlauf] = useState(false);
  const [rohdatenEntryId, setRohdatenEntryId] = useState<string | null>(null);
  const rohdaten = usePoliceClaimsRohdaten(policyId, rohdatenEntryId);

  // Present-but-disabled when the driver is `off` (contract §5 / Ch 7.2): a writer always sees the
  // «neu einholen» button, disabled with the "nicht konfiguriert" hint whenever no provider is wired —
  // not only in the empty state. `query.data` is undefined while loading → treated as not-configured.
  const providerConfigured = query.data?.provider.configured ?? false;
  const header = (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <h3 id="pr-schadenverlauf" className="text-sm font-semibold text-foreground">
        Schadenverlauf
      </h3>
      {canWrite && (
        <Button
          size="sm"
          variant="outline"
          disabled={retrieve.isPending || !providerConfigured}
          onClick={() => retrieve.mutate()}
          data-testid="pr-schadenverlauf-retrieve"
          title={!providerConfigured ? 'Kein Schadenauskunftsdienst konfiguriert' : undefined}
        >
          {retrieve.isPending ? 'Wird eingeholt…' : 'Schadenverlauf neu einholen'}
        </Button>
      )}
    </div>
  );

  let body: React.ReactNode;
  if (query.isLoading) {
    body = <div className="h-20 animate-pulse rounded-md bg-muted" data-testid="pr-schadenverlauf-loading" />;
  } else if (query.isError || !query.data) {
    body = (
      <p role="alert" className="text-sm text-status-danger">
        Schadenverlauf konnte nicht geladen werden.
      </p>
    );
  } else {
    const { current, verlauf, provider } = query.data;
    const older = verlauf.filter((e) => !e.is_current);
    body = (
      <>
        {current === null ? (
          <div className="rounded-md border border-dashed bg-muted/20 p-4 text-sm text-muted-foreground" data-testid="pr-schadenverlauf-empty">
            <p>{SCHADENVERLAUF_NICHT_EINGEHOLT_HINWEIS}</p>
            {!provider.configured && <p className="mt-1">Kein Schadenauskunftsdienst konfiguriert.</p>}
          </div>
        ) : (
          <>
            <SchadenverlaufCard eintrag={current} onShowRohdaten={(id) => setRohdatenEntryId(id)} />
            {!provider.configured && (
              <p className="mt-2 text-sm text-muted-foreground" data-testid="pr-schadenverlauf-not-configured">
                Kein Schadenauskunftsdienst konfiguriert.
              </p>
            )}
          </>
        )}

        {older.length > 0 && (
          <div className="mt-3">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowVerlauf((v) => !v)}
              data-testid="pr-schadenverlauf-verlauf-toggle"
            >
              {showVerlauf ? 'Verlauf ausblenden' : `Verlauf anzeigen (${older.length})`}
            </Button>
            {showVerlauf && (
              <ul className="mt-2 divide-y rounded-md border" data-testid="pr-schadenverlauf-verlauf">
                {older.map((e) => (
                  <li key={e.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 text-sm">
                    <span className="text-muted-foreground">{dateShortDe(e.retrieved_at)}</span>
                    <span>Anzahl: {e.schadenanzahl_5j ?? '-'}</span>
                    <span>Summe: {e.schadensumme_5j === null ? '-' : chf(e.schadensumme_5j)}</span>
                    <span>Quote: {e.schadenquote_rendement === null ? '-' : pct(e.schadenquote_rendement)}</span>
                    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                      {SCHADENVERLAUF_STATUS_LABELS[e.status]}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {retrieve.isError && (
          <p role="alert" className="mt-2 text-sm text-status-danger" data-testid="pr-schadenverlauf-retrieve-error">
            {retrieveErrorMessage(retrieve.error)}
          </p>
        )}
      </>
    );
  }

  return (
    <section aria-labelledby="pr-schadenverlauf" className="rounded-lg border bg-card p-4" data-testid="pr-schadenverlauf">
      {header}
      {body}

      <Dialog
        open={rohdatenEntryId !== null}
        onClose={() => setRohdatenEntryId(null)}
        title="Schadenverlauf - Rohdaten"
        wide
      >
        {rohdaten.isLoading ? (
          <p className="text-sm text-muted-foreground">Lade Rohdaten…</p>
        ) : rohdaten.data ? (
          <div className="space-y-2">
            <pre className="max-h-[50vh] overflow-auto rounded-md bg-muted p-3 text-xs" data-testid="pr-schadenverlauf-rohdaten">
              {JSON.stringify(rohdaten.data.rohdaten_json, null, 2)}
            </pre>
            {rohdaten.data.rohdaten_ref && (
              <p className="text-xs text-muted-foreground">Dokument: {rohdaten.data.rohdaten_ref}</p>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Keine Rohdaten verfügbar.</p>
        )}
      </Dialog>
    </section>
  );
}
